import { useState } from 'react'

import {
  ShiftManagementAreasViewPageDocument,
  ShiftManagementBulkCreatePlanningVersionsDocument,
  ShiftManagementMainPageDocument,
} from 'generated/resources/graphql'
import { EmployeesEmployeeSelectorInput } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { i18nDateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { MINIMUM_EFFECTIVE_AT } from 'modules/time_planning/lib/constants'

import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import PlanningVersionsApi from 'modules/time_planning/store/collections/planningVersions'
import { PLANNING_TOOL } from 'modules/time_planning/store/models/PlanningVersion'

export type IdsType = string[]

export type IdType = string | null | undefined

export type IdsTypes = IdsType | IdType

interface Props {
  effectiveAt: Date
  employeeSelection: EmployeesEmployeeSelectorInput
  numberOfRestDays: number | null
  selectableEmployeesCount: number
  onSuccess: () => void
}

const fetchPlanningVersions = () => {
  // FIXME: Migrate this when we migrate WorkAreas and stop fetching PlanningVersions
  // through mobx-rest
  return asyncAction(() => PlanningVersionsApi.fetch())
}

const useImportEmployeesSM = ({
  effectiveAt,
  employeeSelection,
  numberOfRestDays,
  selectableEmployeesCount,
  onSuccess,
}: Props) => {
  const [, dispatchPagination] = usePagination()
  const [, dispatchUI] = useUI()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchShiftsRows] = useShiftsRows()

  const [dateError, setDateError] = useState<string | undefined>(undefined)

  const [bulkCreatePlanningVersions, { loading }] = useMutation(
    ShiftManagementBulkCreatePlanningVersionsDocument,
    {
      extractorFn: (data) => data.timePlanningMutations.bulkCreateTimeplanningPlanningVersion,
    }
  )

  const clearDateError = () => setDateError(undefined)

  const onSubmit = async () => {
    if (effectiveAt < MINIMUM_EFFECTIVE_AT) {
      const errorMessage = i18n.t('time_planning.planning_versions.errors.effective_at_too_early')
      setDateError(errorMessage)
      app.setFlash({ type: 'wrong', message: errorMessage })
      return
    }

    setDateError(undefined)

    track(trackingEvents.shiftManagement.importEmployeesSubmit, {
      number_of_impacted_employees:
        employeeSelection.type === 'manual' ? (employeeSelection.ids?.length ?? 0) : 'all',
      effective_date: i18nDateToString(effectiveAt, 'backend'),
      number_of_available_employees: selectableEmployeesCount,
    })

    const result = await bulkCreatePlanningVersions({
      variables: {
        effectiveAt: i18nDateToString(effectiveAt, 'backend'),
        employeeSelection,
        numberOfRestDaysInCents: Number(numberOfRestDays),
        planningTool: PLANNING_TOOL.shiftManagement,
      },
      // TODO: Actually update the cache properly
      refetchQueries: [ShiftManagementMainPageDocument, ShiftManagementAreasViewPageDocument],
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('shift_management.actions.adding_employees_success'),
      })

      await fetchPlanningVersions()

      dispatchUI({ type: 'LOADING' })
      dispatchSchedule({ type: 'RESET_VIEW', view })
      dispatchPagination({ type: 'RESET_PAGINATION' })
      dispatchShiftsRows({ type: 'RESET_SHIFTS_ROWS' })

      onSuccess()
    }
  }

  return {
    onSubmit,
    submitting: loading,
    dateError,
    clearDateError,
  }
}

export default useImportEmployeesSM
